import * as React from 'react';
import { Link } from 'react-router';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { TextP } from '../../Defaults/Text';
import {
  FlexColCenter,
  FlexCol,
  FlexRowCenterVertical,
  FlexBetween,
} from '../../Defaults/Flexbox/Flexbox';
import { Tag, TagType } from './ProductTag';
import { ButtonLink } from '../General/Button';
import { StockInfo } from './StockInfo';
import {
  getProductDetailInfo,
  getElasticCategoryProductInfo,
} from '../../../utilities/selectors';
import { getSanitasProductDefaultInfo } from '../selectors';
import { prop } from '../../../utilities';

export interface Props {
  product: any;
  isElasticCategory: boolean;
}

function ProductTabDefault({ product, isElasticCategory }: Props) {
  const { onStock, url, price, oldPrice, unit } =
    isElasticCategory && prop(product, 'product_detail')
      ? getProductDetailInfo(prop(product, 'product_detail'))
      : getProductDetailInfo(product);

  const { name, issale, isnew } = isElasticCategory
    ? getElasticCategoryProductInfo(product)
    : getProductDetailInfo(product);

  const { image } = getSanitasProductDefaultInfo(product);

  const isTag = isnew === 1 || issale === 1;

  return (
    <Wrapper>
      <TopBorder />
      <RightBorder />
      <TopWrapper to={url}>
        <TagsWrapper>
          {isTag ? (
            <>
              {isnew ? <Tag type={TagType.NEW} text={'NOVINKA'} /> : null}
              {issale ? (
                <Tag
                  style={{ marginLeft: isnew ? rem(8) : 0 }}
                  type={TagType.ACTION}
                  text={'AKCIA'}
                />
              ) : null}
            </>
          ) : null}
        </TagsWrapper>
        <Image alt={`Obrázok ku produktu ${name}`} src={image} />
        <Name>{name}</Name>
        <PriceCol>
          <PriceRow>
            <Price>{`${price} €`}</Price>
            {oldPrice ? <OldPrice>{`${oldPrice} €`}</OldPrice> : null}
          </PriceRow>
        </PriceCol>
      </TopWrapper>
      <BottomWrapper>
        <DetailButton to={url}>Detail produktu</DetailButton>
        <StockInfo onStock={onStock} unit={unit} />
      </BottomWrapper>
    </Wrapper>
  );
}

export const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const TopBorder = styled.div`
  border-top: ${({ theme }) => theme.borders.productList};
  position: absolute;
  left: ${rem(32)};
  right: ${rem(32)};
  top: 0;

  ${({ theme }) => theme.mediab.s450`
    left: ${rem(16)};
    right: ${rem(16)};
  `}
`;

const RightBorder = styled.div`
  border-right: ${({ theme }) => theme.borders.productList};
  position: absolute;
  bottom: ${rem(32)};
  top: ${rem(32)};
  right: 0;

  ${({ theme }) => theme.mediab.s450`
    bottom: ${rem(16)};
    top: ${rem(16)};
  `}
`;

const Wrapper = styled(FlexColCenter)`
  animation: ${animateIn} 0.3s ease;
  position: relative;
  width: 100%;
  /* width: ${rem(345)}; */
  width: 33.3333%;
  padding-bottom: ${rem(64)};
  padding-top: ${rem(64)};
  background-color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.mediab.l1400`
    min-width: 50%;
    width: 50%;
    max-width: initial;
  `}

  ${({ theme }) => theme.mediab.s450`
    padding-top: ${rem(32)};
    padding-bottom: ${rem(32)};
  `}

  &:first-child > :first-child,
  &:nth-child(2) > :first-child,
  &:nth-child(3) > :first-child,
  &:nth-child(3n) > :nth-child(2) {
    border: 0;
  }

  &:nth-child(3n+1) {
    ${TopBorder} {
      left: ${rem(15)};
    }
  }

  &:nth-child(3n+3) {
    ${TopBorder} {
      right: ${rem(15)};
    }
  }

  ${({ theme }) => theme.mediab.l1400`
    &:nth-of-type(3) > :first-child {
      border-top: ${theme.borders.productList};
    }

    &:nth-child(3n) > :nth-child(2) {
      border-right: ${theme.borders.productList};
    }

    &:first-child > :first-child,
    &:nth-child(2) > :first-child,
    &:nth-child(2n) > :nth-child(2) {
      border: 0;
    }

    &:nth-child(3n+1) {
      ${TopBorder} {
        left: ${rem(32)};
      }
    }

    &:nth-child(3n+3) {
      ${TopBorder} {
        right: ${rem(32)};
      }
    }
  `}
`;

const TopWrapper = styled(Link)`
  position: relative;
  top: 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  width: 100%;
  height: ${rem(242)};
  max-height: ${rem(270)};
  text-decoration: none;
  margin-bottom: ${rem(16)};

  ${({ theme }) => theme.mediab.s450`
    height: initial;
    margin-bottom: ${rem(8)};
  `}
`;

const BottomWrapper = styled(FlexColCenter)`
  width: 100%;
  justify-content: space-between;
`;

const Image = styled.img`
  margin-top: ${rem(30)};
  margin-bottom: ${rem(24)};
  width: ${rem(143)};
  height: ${rem(143)};
  transition: transform 0.3s ease-out;

  &:hover {
    transform: scale(0.9);
  }

  ${({ theme }) => theme.mediab.s450`
    width: ${rem(128)};
    height: ${rem(128)};
    margin-top: 0;
  `}
`;

const Name = styled(TextP)`
  max-width: 80%;
  font-size: ${rem(14)};
  font-family: ${({ theme }) => theme.font.primary};
  line-height: ${rem(18)};
  letter-spacing: 0px;
  color: ${({ theme }) => theme.colors.black};
  opacity: 1;
  text-align: center;
  margin: 0 auto;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

const PriceCol = styled(FlexCol)`
  margin: ${rem(8)} auto 0 auto;
`;

const PriceRow = styled(FlexRowCenterVertical)``;

const Price = styled(TextP)`
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(24)};
  font-weight: bold;
  line-height: ${rem(28)};
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
`;

const TagsWrapper = styled(FlexBetween)`
  position: absolute;
  top: ${rem(4)};
  left: 8%;
  z-index: 110;
`;

const SecondaryText = styled(TextP)`
  font-family: ${({ theme }) => theme.font.primary};
  font-size: ${rem(12)};
  line-height: ${rem(20)};
  color: ${({ theme }) => theme.colors.placeholder};
`;

const OldPrice = styled(SecondaryText)`
  margin-left: ${rem(8)};
  text-decoration: line-through;
  white-space: nowrap;

  ${({ theme }) => theme.mediab.s450`
    display: none;
  `}
`;

const DetailButton = styled(ButtonLink)`
  height: ${rem(32)};
  background-color: ${({ theme }) => theme.colors.white};
  padding: ${rem(6)} ${rem(12)} ${rem(6)} ${rem(12)};
  margin: 0 auto ${rem(16)} auto;
  color: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${rem(4)};
  font-size: ${rem(14)};
  line-height: ${rem(28)};
  font-weight: 400;
  text-transform: none;
  min-width: ${rem(180)};

  &:hover {
    transition: background-color 0.2s ease;
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }

  ${({ theme }) => theme.mediab.s450`
    display: none;
  `}
`;

export { ProductTabDefault as SanitasProductTab };
